import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icons,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useLogin } from './LoginProvider';
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { useRegistration } from './authHelpers';
import { Registration } from '@ais-ui/api-spec/oceanio';

const FormSchema = z
  .object({
    firstName: z.string().nonempty({ message: 'Required' }),
    lastName: z.string().nonempty({ message: 'Required' }),
    email: z.string().email().nonempty({ message: 'Required' }),
    password: z
      .string()
      .regex(new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{12,}$/), {
        message:
          'Must be at least 12 characters and contain one uppercase and one lowercase character and one number.',
      }),
    companyName: z.string().nonempty({ message: 'Required' }),
    phoneNumber: z.string().nonempty({ message: 'Required' }),
    acceptPrivatePolicy: z.boolean(),
  })
  .required();

export function RegistrationCard() {
  const { showRegistration, setShowRegistration, setShowRegistrationSuccess } =
    useLogin();
  const [acceptedPolicy, setAcceptedPolicy] = useState<boolean>();
  const { mutate: registerUser, data: response } = useRegistration();
  const [didRegister, setDidRegister] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: '',
      password: '',
      companyName: '',
      phoneNumber: '',
      acceptPrivatePolicy: false,
    },
  });

  useEffect(() => {
    if (response?.status === 400) {
      form.setError('root', { message: response.detail });
      setIsLoading(false);
    }

    if (response == null && didRegister) {
      setIsLoading(false);
      setShowRegistrationSuccess(true);
      setShowRegistration(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    setIsLoading(true);
    if (!acceptedPolicy) {
      form.setError('acceptPrivatePolicy', {
        message: 'You must accept the privacy policy',
        type: 'value',
      });
      setIsLoading(false);
    } else {
      setDidRegister(true);
      setIsLoading(false);
      registerUser({ registration: { ...data } });
    }
  };

  return (
    <Dialog open={showRegistration} onOpenChange={setShowRegistration}>
      <DialogContent className="sm:max-w-[425px]">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="w-full space-y-4"
            id="registration"
          >
            <DialogHeader className="text-center py-1 my-2">
              <DialogTitle className="text-2xl">
                Ready to get started?
              </DialogTitle>
              <DialogDescription>
                Enter your information below to register a new account
              </DialogDescription>
            </DialogHeader>

            <div className="flex space-x-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First name<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors('firstName')}>
                      <Input
                        required={true}
                        autoComplete="disabled"
                        type="text"
                        placeholder="First name"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last name<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors('lastName')}>
                      <Input
                        required={true}
                        autoComplete="disabled"
                        type="text"
                        placeholder="Last name"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex space-x-4">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Company name<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl
                      onChange={() => form.clearErrors('companyName')}
                    >
                      <Input
                        required={true}
                        type="text"
                        placeholder="Company name"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Phone Number<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl
                      onChange={() => form.clearErrors('phoneNumber')}
                    >
                      <Input
                        required={true}
                        type="tel"
                        placeholder="Phone number"
                        className="rounded"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    E-mail<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl onChange={() => form.clearErrors('email')}>
                    <Input
                      required={true}
                      autoComplete="disabled"
                      type="text"
                      placeholder="Email"
                      className="rounded"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <div className="flex w-full max-w-sm items-center space-x-2">
                      Password<span className="text-red-500">*</span>{' '}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <QuestionMarkCircledIcon className="ml-2 text-[#3A899B] font-bold" />
                          </TooltipTrigger>
                          <TooltipContent className="bg-[#3A899B] border-[#3A899B] rounded-md text-white">
                            Must be at least 12 characters and contain one
                            uppercase and one lowercase character and one
                            number.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </FormLabel>
                  <FormControl onChange={() => form.clearErrors('password')}>
                    <Input
                      required={true}
                      autoComplete="disabled"
                      type="password"
                      placeholder="Password"
                      className="rounded"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="acceptPrivatePolicy"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <div className="items-top flex space-x-2">
                    <Checkbox
                      onCheckedChange={(value) => {
                        form.clearErrors('acceptPrivatePolicy');
                        setAcceptedPolicy(value ? true : false);
                      }}
                      checked={acceptedPolicy}
                      className="data-[state=checked]:bg-[#3A899B] border-[#3A899B]"
                    />
                    <div className="grid gap-1.5 leading-none">
                      <FormLabel
                        htmlFor="terms1"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Accept terms and conditions
                        <span className="text-red-500">*</span>
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        You agree to our{' '}
                        <a
                          href="https://gatehousemaritime.com/documents/privacy-policy/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          Terms of Use
                        </a>
                        ,{' '}
                        <a
                          href="https://gatehousemaritime.com/documents/privacy-policy/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a
                          href="https://gatehousemaritime.com/documents/cookie-policy/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          Cookie Policy
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-sm">
              <span className="text-red-500">*</span>Required field
            </p>
            {form.formState.errors.root && (
              <div className="text-red-500">
                {form.formState.errors.root.message}
              </div>
            )}
          </form>
        </Form>

        <DialogFooter>
          <Button
            type="submit"
            form="registration"
            variant={'oceanio'}
            className="w-full"
          >
            {isLoading && (
              <Icons.spinner className="mr-2 size-4 animate-spin" />
            )}
            CREATE ACCOUNT
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
